import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import MangaCard from '@/components/feature/MangaCard';
import { getMangaById, getRelatedManga, categories } from '@/mocks/manga';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const manga = id ? getMangaById(id) : undefined;
  const relatedManga = id ? getRelatedManga(id, 4) : [];
  const category = manga ? categories.find((c) => c.slug === manga.category) : undefined;

  if (!manga) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-6xl mx-auto text-center py-20">
            <p className="text-foreground-500">漫画不存在</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${manga.title} - 免费耽美漫画在线看 | 耽美漫画网`;
  const pageDesc = `${manga.title}是耽美漫画网精选的免费BL漫画，由${manga.author}创作。${manga.description.substring(0, 80)}... 耽美漫画网提供高清免费在线阅读。`;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 pb-12 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: manga.title },
            ]}
          />

          <article className="mt-8">
            <div className="flex flex-col md:flex-row gap-8 md:gap-10">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={manga.coverImage}
                    alt={`${manga.title} - 免费耽美漫画在线阅读`}
                    title={`${manga.title} - 耽美漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {manga.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                    manga.status === '已完结' ? 'bg-accent-100 text-accent-700' : 'bg-primary-100 text-primary-700'
                  }`}>
                    {manga.status}
                  </span>
                  <span className="text-sm text-foreground-500">作者：{manga.author}</span>
                  <span className="flex items-center gap-1 text-sm text-accent-500">
                    <i className="ri-star-fill text-xs"></i>
                    {manga.rating}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {manga.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {manga.description}
                </p>

                <div className="flex items-center gap-6 text-xs text-foreground-500 mb-6">
                  <span>
                    <i className="ri-eye-line mr-1"></i>
                    {manga.views > 10000 ? `${(manga.views / 10000).toFixed(1)}万` : manga.views} 次阅读
                  </span>
                  <span>
                    <i className="ri-heart-line mr-1"></i>
                    {manga.favorites > 10000 ? `${(manga.favorites / 10000).toFixed(1)}万` : manga.favorites} 收藏
                  </span>
                  <span>
                    <i className="ri-booklet-line mr-1"></i>
                    共 {manga.chapters.length} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
                    从第一话开始阅读
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-all duration-200 cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line mr-1"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-12">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">章节列表</h2>
              <div className="bg-background-100 rounded-lg divide-y divide-background-200/70">
                {manga.chapters.map((ch, index) => (
                  <div
                    key={ch.id}
                    className="flex items-center justify-between px-4 md:px-5 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-foreground-400 w-12 flex-shrink-0">第{index + 1}话</span>
                      <span className="text-sm text-foreground-700">{ch.title}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{ch.pageCount}页</span>
                      <span>{ch.updateDate}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </article>

          {relatedManga.length > 0 && (
            <section className="mt-14">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedManga.map((rm) => (
                  <MangaCard key={rm.id} manga={rm} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}